<template>
	<NavSidebarLayout
		:scrollable="false"
	>
		<DeferredInvitationBanner
			:show-deferred-invite-notification="isDeferredNotificationVisible"
			:show-successful-invite-notification="isSuccessfulNotificationVisible"
			@invite="onSendInviteClicked"
			@close="onCloseSuccessfulInviteNotificationClicked"
		/>
		<PageCrumbHeaderSkeleton
			v-if="showTitleSkeleton"
		/>
		<TeamPageCrumbHeader
			v-else
			class="one-on-one-layout__page-crumb-header"
			:crumbs="breadcrumbs"
			@back="onBack"
			@crumb="onCrumb"
		>
			<template #body>
				<div class="one-on-one-layout__title-layout">
					<ToggleStar
						v-if="teamName && !team.archived"
						class-name="one-on-one-layout__fave-star"
						:is-toggled="!!team.isFavorited"
						@change="onStarClicked"
					/>
					<div class="one-on-one-layout__team-name" :title="teamName">{{ teamName }}</div>
					<!-- displayed at sm+ breakpoint -->
					<div class="one-on-one-layout__user-avatar">
						<UserAvatar
							:user="teammate"
							size="1.5rem"
						/>
					</div>
					<div v-if="showSampleChip" class="one-on-one-layout__sample-chip-container">
						<!-- displayed at sm+ breakpoint -->
						<TeamingChip class="one-on-one-layout__sample-chip">
							<template v-slot:text>{{ translate( "sampleTeamChip" ) }}</template>
						</TeamingChip>
					</div>
				</div>
			</template>
		</TeamPageCrumbHeader>

		<section class="one-on-one-layout__tab-nav-row">
			<TabSet :tabs="tabs" @select="onNavItemClick" />
		</section>

		<slot name="view" :team="team" :team-loaded="teamLoaded">
			<main class="one-on-one-layout__tab-container">
				<router-view v-if="teamLoaded"
					:teamId="teamId"
					:team="team"
					:key="teamId"
					v-bind="$attrs"
				/>
			</main>
		</slot>
	</NavSidebarLayout>
</template>

<script>
import compact from "lodash/compact";
import ScreenSizeDetector from "screen-size-detector";
import {
	mapActions,
	mapGetters,
	mapMutations
} from "vuex";

import {
	TeamingButtonType
} from "@teaming/common-enums";

import {
	DeferredInvitationBanner,
	DocumentMixin,
	PageCrumbHeaderSkeleton,
	TabSet,
	TeamingChip,
	ToggleStar,
	TranslateMixin,
	UserAvatar,
	getRelativeOneOnOneMembers,
	makeCrumb
} from "@teaming/common-ui";

import {
	TeamPageCrumbHeader
} from "@teaming/common-team";

export default {
	name: "OneOnOneLayout",
	teamingPackage: "slice-oneonone.layouts",
	mixins: [ TranslateMixin, DocumentMixin, ],
	components: {
		DeferredInvitationBanner,
		PageCrumbHeaderSkeleton,
		TabSet,
		TeamPageCrumbHeader,
		TeamingChip,
		ToggleStar,
		UserAvatar,
	},
	inject: [ "userProfile", ],
	data() {
		return {
			screenWidth: window.innerWidth,
			teamLoaded: false,
		};
	},
	props: {
		teamId: {
			type: String,
			required: true,
		},
	},
	watch: {
		teamId( newTeamId, oldTeamId ) {
			if ( newTeamId && newTeamId !== oldTeamId ) {
				this.teamLoaded = false;
				this.applyTeamId( newTeamId );
				this.fetchOneOnOne();
			}
		},
		team( newTeam, oldTeam ) {
			if ( newTeam && newTeam.id && newTeam.id !== oldTeam.id ) {
				this.teamLoaded = true;
			}
		},
		teamName() {
			this.sendTelemetry();
			// see DocumentMixin
			this.trySetDocumentTitle();
		},
		$route() {
			this.sendTelemetry();
		},
	},
	computed: {
		...mapGetters( "one-on-one-layout", [
			"team",
			"teamName",
			"activeGoalCount",
			"outstandingCommitmentCount",
			"isDeferredNotificationVisible",
			"isSuccessfulNotificationVisible",
			"deferredMember",
		] ),
		// see: DocumentMixin
		// eslint-disable-next-line vue/no-unused-properties
		documentTitle() {
			const { teamName, } = this;
			if ( !teamName ) {
				return "";
			}
			return this.translate( "documentTitle", { name: teamName, } );
		},
		breadcrumbs() {
			const { team, } = this;
			return [
				makeCrumb( {
					text: this.translate( "breadcrumbText" ),
					route: "teams",
				} ),
				makeCrumb( {
					text: this.translate( "teamBreadcrumbText", team ),
					isActive: true,
				} ),
			];
		},
		tabs() {
			if ( !( this.userProfile?.personId && this.team?.id ) ) {
				return [];
			}

			const { navigation, } = this.$foundation();
			const route = navigation.getCurrentRoute().path;
			const isTeammate = /teammate/i.test( route );
			const isCommitments = /commitments/i.test( route );
			const isGoals = /goals/i.test( route );
			const isWorkstyles = /workstyles/i.test( route );
			const isSettings = /settings/i.test( route );
			const isStream = /stream/i.test( route );
			const isTimeline = /timeline/i.test( route );

			const teammateName = this.teammate?.shortDisplayName;
			const teammateTabLabel = teammateName ?
				this.translate( "aboutTab", { name: teammateName, } ) :
				this.translate( "teammateTab" );

			const tabs = [
				{
					id: "stream",
					label: this.translate( "streamTab" ),
					isActive: isStream,
				},
				{
					id: "workstyle",
					label: this.translate( "workstyleTab" ),
					isActive: isWorkstyles,
				},
				{
					id: "teammate",
					label: teammateTabLabel,
					isActive: isTeammate,
				},
				{
					id: "commitments",
					label: this.translate( "commitmentsTab" ),
					isActive: isCommitments,
					count: this.outstandingCommitmentCount,
				},
				{
					id: "goals",
					label: this.translate( "goalsTab" ),
					isActive: isGoals,
					count: this.activeGoalCount,
				},
				{
					id: "settings",
					label: this.translate( "settingsTab" ),
					isActive: isSettings,
				},
			];
			if ( this.team.shouldShowTimeline ) {
				tabs.push( {
					id: "timeline",
					label: this.translate( "timelineTab" ),
					isActive: isTimeline,
				} );
			}
			return compact( tabs );
		},
		teammate() {
			const { personId: thisMemberId, } = this.userProfile;
			const { otherMember, } = getRelativeOneOnOneMembers( this.team, thisMemberId );
			return otherMember;
		},
		showTitleSkeleton() {
			return !this.teamName;
		},
		showSampleChip() {
			return this.teamName && this.team.isTeamingTeam;
		},
	},
	methods: {
		...mapMutations( "one-on-one-layout", [
			"applyTeamId",
			"hideSuccessfulInviteNotification",
			"resetStoreState",
			"showSuccessfulInviteNotification",
		] ),
		...mapActions( "one-on-one-layout", [
			"tryFavoriteOneOnOne",
			"tryUnfavoriteOneOnOne",
		] ),
		/* @TODO: move to vuex action */
		sendTelemetry() {
			const { team, userProfile, } = this;
			if ( !team?.id ) {
				return;
			}

			const { telemetry, } = this.$foundation();
			const telemetryEvents = {
				commitments: telemetry.oneonone.view1on1CommitmentsTab,
				goals: telemetry.oneonone.view1on1GoalsTab,
				settings: telemetry.oneonone.view1on1SettingsTab,
				stream: telemetry.oneonone.view1on1StreamTab,
				teammate: telemetry.oneonone.view1on1TeammateTab,
				workstyle: telemetry.oneonone.view1on1WorkStyleTab,
			};

			const { tabs, } = this;
			const activeTab = tabs.find( tab => tab.isActive );
			if ( !activeTab ) {
				return;
			}

			const telemetryEvent = telemetryEvents[ activeTab.id ];
			if ( !telemetryEvent ) {
				return;
			}

			const {
				id: teamId,
				name: teamName,
				members,
				isTeamingTeam: isSampleTeam,
			} = team;

			const { personId, } = userProfile;
			const thisMemberWorkStyleType = userProfile.assessments?.DISC?.moniker || "";
			const thisMember = members.find( member => member.personId === personId );
			const { role: thisMemberRole, } = thisMember;
			const otherMember = members.find( member => member.personId !== personId );
			const otherMemberWorkStyleType = otherMember.assessments?.DISC?.moniker || "";

			telemetryEvent( {
				teamId,
				teamName,
				isSampleTeam,
				otherMemberWorkStyleType,
				thisMemberWorkStyleType,
				thisMemberRole,
			} );
		},
		fetchOneOnOne() {
			const { teamId, } = this;
			this.applyTeamId( teamId );
			const { ioTeam, ioGokr, ioOutcome, } = this.$foundation();
			this.$nextTick( () => {
				ioTeam.fetchOneOnOne( { teamId, refetch: true, } );
				ioGokr.fetchGoalMetadata();
				ioOutcome.fetchCommitmentMetadata( { teamId, } );
			} );
		},
		onStarClicked( e ) {
			if ( e.isToggled ) {
				this.tryFavoriteOneOnOne( { oneOnOneId: this.teamId, } );
			} else {
				this.tryUnfavoriteOneOnOne( { oneOnOneId: this.teamId, } );
			}
		},
		onSendInviteClicked() {
			const { deferredMember, team, } = this;
			if ( !deferredMember ) {
				return;
			}

			const { ioTeam, dialogs, } = this.$foundation();

			const sendTheNotification = () => {
				const args = {
					teamId: team.id,
					member: deferredMember,
				};
				ioTeam.inviteDeferredMember( args );
				this.showSuccessfulInviteNotification();
				setTimeout( () => {
					this.hideSuccessfulInviteNotification();
					// eslint-disable-next-line no-magic-numbers
				}, 5000 );
			};

			dialogs.requestUserConfirmation( {
				header: "slice-oneonone.layouts.OneOnOneLayout.deferredInvitationModalPromptHeader",
				description: "slice-oneonone.layouts.OneOnOneLayout.deferredInvitationModalPromptDescription",
				actions: [
					{
						id: "send",
						type: TeamingButtonType.PRIMARY,
						label: "slice-oneonone.layouts.OneOnOneLayout.sendDeferredInvitationButtonText",
						isDestructive: false,
						onButtonPress: sendTheNotification,
					},
					{
						id: "cancel",
						type: TeamingButtonType.QUATERNARY,
						label: "slice-oneonone.layouts.OneOnOneLayout.cancelDeferredInvitationButtonText",
						isDestructive: false,
						onButtonPress: () => {},
					},
				],
				metadata: {
					email: deferredMember.email,
				},
			} );
		},
		onCloseSuccessfulInviteNotificationClicked() {
			this.hideSuccessfulInviteNotification();
		},
		onBack() {
			const { navigation, } = this.$foundation();
			navigation.navigateToOneOnOnes();
		},
		onCrumb( { route, } ) {
			if ( route === "teams" ) {
				this.onBack();
			}
		},
		onNavItemClick( navId ) {
			const { navigation, } = this.$foundation();
			const idToNavMap = {
				stream: navigation.navigateToOneOnOneSharedStream,
				workstyle: navigation.navigateToOneOnOneWorkstyles,
				teammate: navigation.navigateToOneOnOneTeammate,
				commitments: navigation.navigateToOneOnOneCommitments,
				goals: navigation.oneOnOne.gokr.navigateToGoals,
				settings: navigation.navigateToOneOnOneSettings,
				timeline: navigation.oneOnOne.navigateToTimeline,
			};
			const navigate = idToNavMap[ navId ];
			if ( !navigate ) {
				// eslint-disable-next-line no-console
				return console.error( `no navigation available for ID: ${ navId }` );
			}
			navigate( { teamId: this.teamId, } );
		},
	},
	created() {
		this.screen = new ScreenSizeDetector( {
			onWidthChange: () => {
				this.screenWidth = this.screen.width;
			},
		} );
		this.applyTeamId( this.teamId );
		this.fetchOneOnOne();
		if ( this.teamId === this.team?.id ) {
			this.teamLoaded = true;
		}
	},
	mounted() {
		this.sendTelemetry();
	},
	beforeUnmount() {
		this.resetStoreState();
	},
	unmounted() {
		this.screen.removeMainCallback( "widthchange" );
		this.screen = undefined;
	},
};
</script>

<style lang="scss">
#app {
	-webkit-font-smoothing: antialiased;
	-moz-osx-font-smoothing: grayscale;
	height: 100%;
}
</style>

<style lang="scss" scoped>
$oneOnOneLayoutTitleFontColor: $color1-700;
$oneOnOneLayoutUnfavedStarColor: $color1-300;
$oneOnOneLayoutNavBorderColor: $color1-200;
$oneOnOneLayoutMenuIconColor: $color2-500;
$oneOnOneLayoutSampleChipFontColor: $color2-600;
$oneOnOneLayoutSampleChipBGColor: $color2-100;
$oneOnOneLayoutListingLinkFontColor: $color2-500;
$oneOnOneLayoutListingLinkDividerColor: $color1-400;

.one-on-one-layout__tab-container {
	@include auto-y-scroll();
	width: 100%;
	flex: 1;
	position: relative;
	display: flex;
	flex-direction: column;
}

.one-on-one-layout__title-layout {
	display: flex;
	flex-direction: row;
	justify-content: flex-start;
	align-items: center;
	width: 100%;

	gap: 0.5rem;
}

.one-on-one-layout__team-name {
	font-weight: bold;
	overflow: hidden;
	white-space: nowrap;
	text-overflow: ellipsis;

	@include font-and-line-for(2);

	@include for-size('md') {
		max-width: unset;
	}
}

.one-on-one-layout__sample-chip-container {
	display: none;

	@include for-size-and-larger('sm') {
		display: block;
	}
}

.one-on-one-layout__sample-chip {
	@include font-and-line-for(0);
	text-transform: uppercase;
	color: $oneOnOneLayoutSampleChipFontColor;
	background: $oneOnOneLayoutSampleChipBGColor;
	border-radius: 1.5rem;
	font-weight: 600;
}

.one-on-one-layout__fave-star {
	color: $oneOnOneLayoutUnfavedStarColor;
	cursor: pointer;
}

.one-on-one-layout__tab-nav-row {
	padding: 0 1rem 0 1rem;
	@include auto-x-scroll();
	overflow-y: hidden;
	border-bottom: .0625rem solid $oneOnOneLayoutNavBorderColor;
	text-align: left;
}

.one-on-one-layout__page-crumb-header {
	border-bottom: none;
}
</style>
